import { Icon } from '@natera/material/lib/icon'
import { format, parse } from 'date-fns'
import type { ReactNode } from 'react'
import {
  getCarcOptions,
  getFrequentlyUsedCarcCodes,
  getFrequentlyUsedNateraTagCodes,
  getFrequentlyUsedRarcCodes,
  getNateraTagOptions,
  getRarcOptions,
} from '~/entities/carc-rarc'
import type { Subcategory } from '~/entities/categories'
import { DATA_EXTRACTION_FIELDS_CONFIG, type DataExtractionFieldName } from '~/entities/document'
import { SuggestedPayorField } from '~/entities/payors'
import { cn } from '~/shared/lib/cn'
import { withForm } from '~/shared/lib/form'
import { DateInput, MultiSelect, Select, Text, Textarea, Textfield, Tooltip } from '~/shared/ui'
import { CONFIDENCE_SCORE_THRESHOLD, type Subgroup } from '../../../config'
import type { DataExtractionMode } from '../../types'
import { FieldLayout } from './field-layout'

const FREQUENTLY_USED_CODES_LIMIT = 5

function getFieldTooltip(fieldName: DataExtractionFieldName): string | undefined {
  const entry = DATA_EXTRACTION_FIELDS_CONFIG[fieldName]
  return 'tooltip' in entry ? entry.tooltip : undefined
}

function withTooltip(tooltip: string | undefined, children: ReactNode): ReactNode {
  if (!tooltip) {
    return children
  }
  return (
    <div className="relative">
      <Tooltip content={<div className="max-w-[320px] whitespace-pre-line">{tooltip}</div>}>
        <Icon
          children="info"
          className="text-content-high-emphasis absolute top-[17px] left-4 z-1 cursor-pointer"
        />
      </Tooltip>
      {children}
    </div>
  )
}

export const FormFields = withForm({
  defaultValues: {} as Record<DataExtractionFieldName, string | undefined>,
  props: {
    fields: [] as Subgroup['fields'],
    fieldsMap: {} as Record<
      string,
      | {
          value: string | undefined
          confidence: number
          recommendedValue?: string
          required: boolean
        }
      | undefined
    >,
    setHistoryValues: (_values: Record<string, string | undefined>) => {},
    categoryMetadata: null as Subcategory['metadata'] | null,
    mode: 'view' as DataExtractionMode,
    toggleFieldActive: (_fieldName: string) => {},
    activeFieldKeys: [] as string[],
  },
  render: function Render({
    form,
    fields,
    fieldsMap,
    setHistoryValues,
    categoryMetadata,
    mode,
    toggleFieldActive,
    activeFieldKeys,
  }) {
    return (
      <>
        {fields.map((field) => {
          const responseField = fieldsMap[field.key]
          // We filter out non-present fields before form initialization, here is just the type-check
          if (!responseField) {
            return null
          }
          const confidence = responseField.confidence
          const active = activeFieldKeys.includes(field.key)
          switch (DATA_EXTRACTION_FIELDS_CONFIG[field.key]?.type) {
            case 'textfield':
              return (
                <>
                  {field.key === 'insurance_company' && fieldsMap['payor_id'] && (
                    <form.Field
                      key="payor_id"
                      name="payor_id"
                      children={(formField) => (
                        <FieldLayout
                          mode={mode}
                          required={responseField.required}
                          active={active}
                          fieldKey="payor_id"
                          fullRowField={field.fullRow}
                          toggleFieldActive={toggleFieldActive}
                        >
                          <SuggestedPayorField
                            label={DATA_EXTRACTION_FIELDS_CONFIG.payor_id.label}
                            name="payor_id"
                            value={formField.state.value}
                            onChange={(value) => {
                              formField.handleChange(value)
                              setHistoryValues({ [formField.name]: value })
                            }}
                            onBlur={() => {
                              formField.handleBlur()
                            }}
                          />
                        </FieldLayout>
                      )}
                    />
                  )}
                  <form.Field
                    key={field.key}
                    name={field.key}
                    children={(formField) => {
                      const tooltip = getFieldTooltip(formField.name)
                      return (
                        <FieldLayout
                          mode={mode}
                          required={responseField.required}
                          active={active}
                          fieldKey={field.key}
                          fullRowField={field.fullRow}
                          toggleFieldActive={toggleFieldActive}
                        >
                          {withTooltip(
                            tooltip,
                            <Textfield
                              label={
                                field.customLabel ??
                                DATA_EXTRACTION_FIELDS_CONFIG[formField.name].label
                              }
                              name={formField.name}
                              value={formField.state.value}
                              onChange={(e) => {
                                formField.handleChange(e.target.value)
                              }}
                              onBlur={() => {
                                formField.handleBlur()
                                setHistoryValues({ [formField.name]: formField.state.value })
                              }}
                              help={confidence < CONFIDENCE_SCORE_THRESHOLD}
                              leadingIcon={tooltip ? <span /> : undefined}
                            />,
                          )}
                        </FieldLayout>
                      )
                    }}
                  />
                </>
              )
            case 'select': {
              const fieldConfig = DATA_EXTRACTION_FIELDS_CONFIG[field.key]
              if (fieldConfig.type !== 'select') {
                return null
              }
              // `peer_to_peer_indicator` is always treated as required so the arrow
              // toggle in FieldLayout is disabled and the field stays in the left
              // column (spec 023). Other select fields keep the server flag.
              const isPeerToPeerIndicator = field.key === 'peer_to_peer_indicator'
              return (
                <form.Field
                  key={field.key}
                  name={field.key}
                  children={(formField) => (
                    <FieldLayout
                      mode={mode}
                      required={isPeerToPeerIndicator ? true : responseField.required}
                      active={active}
                      fieldKey={field.key}
                      fullRowField={field.fullRow}
                      toggleFieldActive={toggleFieldActive}
                    >
                      <Select
                        label={field.customLabel ?? fieldConfig.label}
                        name={formField.name}
                        value={formField.state.value ?? ''}
                        onValueChange={(value) => {
                          const nextValue = String(value)
                          formField.handleChange(nextValue)
                          setHistoryValues({ [formField.name]: nextValue })
                        }}
                        onBlur={() => {
                          formField.handleBlur()
                        }}
                      >
                        {fieldConfig.options.map((option) => (
                          <Select.Item
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </Select.Item>
                        ))}
                      </Select>
                    </FieldLayout>
                  )}
                />
              )
            }
            case 'date-input':
              return (
                <form.Field
                  key={field.key}
                  name={field.key}
                  children={(formField) => {
                    const tooltip = getFieldTooltip(formField.name)
                    return (
                      <FieldLayout
                        mode={mode}
                        required={responseField.required}
                        active={active}
                        fieldKey={field.key}
                        fullRowField={field.fullRow}
                        toggleFieldActive={toggleFieldActive}
                      >
                        {withTooltip(
                          tooltip,
                          <DateInput
                            data-track-name={formField.name}
                            label={
                              field.customLabel ??
                              DATA_EXTRACTION_FIELDS_CONFIG[formField.name].label
                            }
                            name={formField.name}
                            date={
                              formField.state.value
                                ? parse(formField.state.value, 'yyyy-MM-dd', new Date())
                                : undefined
                            }
                            onDateChange={(date) => {
                              const value = date ? format(date, 'yyyy-MM-dd') : ''
                              formField.handleChange(value)
                              setHistoryValues({ [formField.name]: value })
                            }}
                            help={confidence < CONFIDENCE_SCORE_THRESHOLD}
                            leadingIcon={tooltip ? <span /> : undefined}
                          />,
                        )}
                      </FieldLayout>
                    )
                  }}
                />
              )
            case 'carc':
              return (
                <form.Field
                  key={field.key}
                  name={field.key}
                  validators={{
                    onChange: ({ value }) => {
                      if (!value) {
                        return 'Required'
                      }
                    },
                    onMount: ({ value }) => {
                      if (!value) {
                        return 'Required'
                      }
                    },
                  }}
                  children={(formField) => {
                    const serverValues = fieldsMap.carc_codes?.value?.split(',')
                    const frequentValues = getFrequentlyUsedCarcCodes(categoryMetadata).slice(
                      0,
                      FREQUENTLY_USED_CODES_LIMIT,
                    )
                    return (
                      <FieldLayout
                        mode={mode}
                        required={responseField.required}
                        active={active}
                        fieldKey={field.key}
                        fullRowField={field.fullRow}
                        toggleFieldActive={toggleFieldActive}
                      >
                        <MultiSelect
                          label={
                            field.customLabel ?? DATA_EXTRACTION_FIELDS_CONFIG.carc_codes.label
                          }
                          placeholder={DATA_EXTRACTION_FIELDS_CONFIG.carc_codes.placeholder}
                          hint={DATA_EXTRACTION_FIELDS_CONFIG.carc_codes.hint}
                          required
                          options={getCarcOptions(serverValues)}
                          frequentValues={frequentValues}
                          frequentGroupLabel="Frequently Used AMD and x12 CARCs"
                          recommendedValues={
                            fieldsMap.carc_codes?.recommendedValue
                              ? fieldsMap.carc_codes.recommendedValue.split(',')
                              : []
                          }
                          recommendedGroupLabel="opti Suggested CARCs"
                          value={formField.state.value ? formField.state.value.split(',') : []}
                          onValueChange={(value) => {
                            const fieldValue = value.join(',')
                            formField.handleChange(fieldValue)
                            if (value.length === 0) {
                              formField.form.setFieldValue('rarc_codes', '')
                              setHistoryValues({
                                [formField.name]: fieldValue,
                                rarc_codes: '',
                              })
                            } else {
                              setHistoryValues({
                                [formField.name]: fieldValue,
                              })
                            }
                          }}
                          error={
                            formField.state.meta.isTouched
                              ? formField.state.meta.errors.join(', ')
                              : undefined
                          }
                        />
                      </FieldLayout>
                    )
                  }}
                />
              )
            case 'rarc':
              return (
                <form.Field
                  key={field.key}
                  name={field.key}
                  children={(formField) => {
                    const serverValues = fieldsMap.rarc_codes?.value?.split(',')
                    const frequentValues = getFrequentlyUsedRarcCodes(categoryMetadata).slice(
                      0,
                      FREQUENTLY_USED_CODES_LIMIT,
                    )
                    return (
                      <form.Subscribe
                        selector={(state) => state.values.carc_codes}
                        children={(carcCodes) => (
                          <FieldLayout
                            mode={mode}
                            required={responseField.required}
                            active={active}
                            fieldKey={field.key}
                            fullRowField={field.fullRow}
                            toggleFieldActive={toggleFieldActive}
                          >
                            <MultiSelect
                              label={
                                !carcCodes
                                  ? 'Choose a CARC to enable RARC selection'
                                  : (field.customLabel ??
                                    DATA_EXTRACTION_FIELDS_CONFIG.rarc_codes.label)
                              }
                              placeholder={DATA_EXTRACTION_FIELDS_CONFIG.rarc_codes.placeholder}
                              hint={DATA_EXTRACTION_FIELDS_CONFIG.rarc_codes.hint}
                              disabled={!carcCodes}
                              options={getRarcOptions(serverValues)}
                              frequentValues={frequentValues}
                              frequentGroupLabel="Frequently Used AMD and x12 RARCs"
                              recommendedValues={
                                fieldsMap.rarc_codes?.recommendedValue
                                  ? fieldsMap.rarc_codes.recommendedValue.split(',')
                                  : []
                              }
                              recommendedGroupLabel="opti Suggested RARCs"
                              value={formField.state.value ? formField.state.value.split(',') : []}
                              onValueChange={(value) => {
                                const fieldValue = value.join(',')
                                formField.handleChange(fieldValue)
                                setHistoryValues({ [formField.name]: fieldValue })
                              }}
                            />
                          </FieldLayout>
                        )}
                      />
                    )
                  }}
                />
              )
            case 'natera-tags': {
              const serverValues = fieldsMap.natera_tags?.value?.split(',')
              const nateraTagOptions = getNateraTagOptions(categoryMetadata, serverValues)
              return (
                <form.Field
                  key={field.key}
                  name={field.key}
                  validators={{
                    onChange: ({ value }) => {
                      if (!value && nateraTagOptions.length > 0) {
                        return 'Required'
                      }
                    },
                    onMount: ({ value }) => {
                      if (!value && nateraTagOptions.length > 0) {
                        return 'Required'
                      }
                    },
                  }}
                  children={(formField) => {
                    const frequentValues = getFrequentlyUsedNateraTagCodes(categoryMetadata).slice(
                      0,
                      FREQUENTLY_USED_CODES_LIMIT,
                    )
                    return (
                      <FieldLayout
                        mode={mode}
                        required={responseField.required}
                        active={active}
                        fieldKey={field.key}
                        fullRowField={field.fullRow}
                        toggleFieldActive={toggleFieldActive}
                      >
                        <MultiSelect
                          label={
                            field.customLabel ?? DATA_EXTRACTION_FIELDS_CONFIG.natera_tags.label
                          }
                          placeholder={DATA_EXTRACTION_FIELDS_CONFIG.natera_tags.placeholder}
                          required={nateraTagOptions.length > 0}
                          options={nateraTagOptions}
                          value={formField.state.value ? formField.state.value.split(',') : []}
                          frequentValues={frequentValues}
                          frequentGroupLabel="Frequently Used Natera Tags"
                          recommendedValues={
                            fieldsMap.natera_tags?.recommendedValue
                              ? fieldsMap.natera_tags.recommendedValue.split(',')
                              : []
                          }
                          recommendedGroupLabel="opti Suggested Natera Tags"
                          onValueChange={(value) => {
                            const fieldValue = value.join(',')
                            formField.handleChange(fieldValue)
                            setHistoryValues({
                              [formField.name]: fieldValue,
                            })
                          }}
                          error={
                            formField.state.meta.isTouched
                              ? formField.state.meta.errors.join(', ')
                              : undefined
                          }
                        />
                      </FieldLayout>
                    )
                  }}
                />
              )
            }
            case 'mandatory-comment': {
              return (
                <form.Field
                  key={field.key}
                  name={field.key}
                  children={(formField) => {
                    return (
                      <div className={cn('flex flex-col gap-2', { 'col-span-2': field.fullRow })}>
                        <Text appearance="CAPTION">
                          Help us understand why you've made a change to the suggested codes or
                          tags. This will support us in training opti so that the suggestions are
                          more accurate in the future.
                          <span className="text-error-main">*</span>
                        </Text>
                        <Textarea
                          className="h-[64px] resize-none"
                          placeholder="Add your thoughts here."
                          filled
                          value={formField.state.value}
                          onChange={(e) => formField.handleChange(e.target.value)}
                          onBlur={(e) => {
                            formField.handleBlur()
                            setHistoryValues({ natera_tags_notes: e.target.value })
                          }}
                        />
                      </div>
                    )
                  }}
                />
              )
            }
            default:
              return null
          }
        })}
      </>
    )
  },
})
